import { Box, Flex, Image, Text } from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import iconFile_white from '~/assets/icons/icon-picture-gray.svg'
import { ImageIcon } from '~/components/ImageIcon'
import { emojiParser } from '~/constants/parsers'
import {
  GradientBackgroundSendingAnimation,
  PendingMessageBubbleVariants,
  usePendingMessageBubbleStyles,
} from '~/pages/Messaging/componentStyles/PendingMessageBubble.style'
import { PendingMessage } from '~/pages/Messaging/hooks/usePendingMessages'
import { fileToBase64 } from '~/utils/formatter'
import { noop } from '~/utils/functions'

interface PendingMessageBubbleProps {
  message: PendingMessage
  onRetry?: (message: PendingMessage) => void
}
const PendingMessageBubble = ({
  message,
  onRetry,
}: PendingMessageBubbleProps) => {
  // Count of emojis for the current message.
  const { enlargeEmojies } = emojiParser(message.body)
  const [fileUrl, setFileUrl] = useState<string | undefined>(undefined)

  const isFailed = message.status === 'failed'
  const canRetry = message.originalSendParams !== undefined
  const hasBody = message.body && message.body !== ''
  const isEnlarged =
    enlargeEmojies || (message.messageType === 'file' && !hasBody)

  let variant: PendingMessageBubbleVariants
  if (isFailed) {
    variant = isEnlarged
      ? PendingMessageBubbleVariants.FailedEnlarged
      : PendingMessageBubbleVariants.Failed
  } else {
    variant = isEnlarged
      ? PendingMessageBubbleVariants.SendingEnlarged
      : PendingMessageBubbleVariants.Sending
  }

  const styles = usePendingMessageBubbleStyles({
    variant: variant,
  })

  /// Use effect for loading the attached file into a base64 data64 image url, if one exists.
  useEffect(() => {
    if (message.originalSendParams?.file === undefined) {
      return
    }

    let isMounted = true

    const loadFile = async (file: File) => {
      const dataURL = (await fileToBase64(file)) as string
      if (!isMounted) {
        return
      }
      setFileUrl(dataURL)
    }

    loadFile(message.originalSendParams?.file)
    return () => {
      isMounted = false
    }
  })

  const handleRetry = () => {
    if (message.originalSendParams === undefined) {
      return
    }
    onRetry?.(message)
  }

  return (
    <Flex sx={styles.container}>
      <Text
        sx={styles.statusText}
        onClick={isFailed && canRetry ? handleRetry : noop}
      >
        {isFailed ? 'Failed to send. ' : 'Sending...'}
        {isFailed && canRetry && <Text sx={styles.retryText}>Retry</Text>}
        {isFailed ? '?' : ''}
      </Text>
      <Box
        sx={styles.bubble}
        animation={isFailed ? undefined : GradientBackgroundSendingAnimation}
      >
        <Flex sx={styles.messageTextWrapper}>
          {hasBody && <Text sx={styles.messageText}>{message.body}</Text>}
          {message.messageType === 'file' &&
            (fileUrl ? (
              <Image sx={styles.messageImage} src={fileUrl} />
            ) : (
              <ImageIcon icon={iconFile_white} width={42} height={42} />
            ))}
        </Flex>
      </Box>
    </Flex>
  )
}

export default PendingMessageBubble
